// frontend/src/pages/admin/ApprovalWorkflows.js
/**
 * Approval Workflow Management — Admin Page
 *
 * Three-tab layout following design-system-reference.md:
 *   1. Workflow Templates  — create, edit, version, duplicate workflows
 *   2. Assignment Matrix   — map workflows to departments / trades / individuals
 *   3. Employee Preview    — test which workflow resolves for any employee
 *
 * Styling: Admin Theme (Red) per design-system-reference.md
 */
import React, { useState, useMemo } from 'react';
import {
  GitBranch,
  Plus,
  Search,
  Edit,
  Trash2,
  Copy,
  Eye,
  History,
  Users,
  Building2,
  Wrench,
  UserCheck,
  ChevronDown,
  ChevronRight,
  X,
  CheckCircle,
  AlertTriangle,
  Clock,
  ArrowRight,
  Shield,
  Layers,
  Target,
  Info,
} from 'lucide-react';
import DatePickerField from '../../components/UI/DatePickerField';
import FormSelect from '../../components/UI/FormSelect';
import PillSelect from '../../components/UI/PillSelect';
import SearchableCombobox from '../../components/UI/SearchableCombobox';
import { useTabPersistence } from '../../hooks/usePersistence';
import {
  useApprovalWorkflows,
  useActiveWorkflows,
  useWorkflowVersionHistory,
  useWorkflowSummary,
  useCreateWorkflow,
  useUpdateWorkflow,
  useDeleteWorkflow,
  useDuplicateWorkflow,
  useWorkflowAssignments,
  useCreateAssignment,
  useUpdateAssignment,
  useDeleteAssignment,
  usePreviewWorkflowForEmployee,
} from '../../hooks/useApprovalWorkflowData';
import { useDepartments, useTradeCategories } from '../../hooks/useConfigData';
import { useUpdateStaff, useStaffList } from '../../hooks';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Modal from '../../components/UI/Modal';
import toast from '../../utils/toast';
import ConfirmModal from '../../components/UI/ConfirmModal';
import { formatDate, toLocalISODate, parseLocalDate, getTodayInSingaporeTimezone } from '../../utils/dateUtils';
import { CountBadge } from '../../components/UI/CountBadge';
import { FilterButton } from '../../components/UI/FilterButton';
import DebouncedSearchInput from '../../components/UI/DebouncedSearchInput';

// ─── Constants ──────────────────────────────────────────────────────────

const VALID_TABS = ['templates', 'assignments', 'preview'];

const APPROVER_ROLE_LABELS = {
  reporting_officer: 'Reporting Officer',
  line_manager: 'Line Manager',
  department_head: 'Department Head',
  hr_manager: 'HR Manager',
  finance_manager: 'Finance Manager',
  trade_leader: 'Trade Leader',
  deputy_hod: 'Deputy HOD',
};

const CLASSIFICATION_LABELS = {
  staff: 'Staff',
  worker: 'Worker',
  management: 'Management',
  executive: 'Executive',
  all: 'All Classifications',
};

const ASSIGNMENT_TYPE_LABELS = {
  classification_default: 'Classification Default',
  trade_category: 'Trade Category',
  department: 'Department Override',
  individual: 'Individual Override',
};

const PRIORITY_COLORS = {
  individual: 'bg-purple-100 text-purple-700',
  department: 'bg-blue-100 text-blue-700',
  trade_category: 'bg-amber-100 text-amber-700',
  classification_default: 'bg-gray-100 text-gray-700',
};

/** Threshold sentinel — 999 means "all leave durations" */
const THRESHOLD_SENTINEL = 999;

/** Status badge for an assignment row — shared by desktop table and mobile cards. */
const renderAssignmentStatusBadge = (a) =>
  a.is_expired ? (
    <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">Expired</span>
  ) : a.is_active ? (
    <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">Active</span>
  ) : (
    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Inactive</span>
  );

/**
 * Human-readable threshold label for approval chain pills.
 * Hides sentinel values (999) and returns a helpful short text.
 */
const formatThreshold = (days) => {
  if (!days || days >= THRESHOLD_SENTINEL) return null; // "All" — don't show
  return `≤${days}d`;
};

// ─── Main Component ─────────────────────────────────────────────────────

const ApprovalWorkflows = () => {
  const [activeTab, setActiveTab] = useTabPersistence(
    'approval-workflows',
    'templates',
    VALID_TABS
  );

  const { data: summary, isLoading: summaryLoading } = useWorkflowSummary();

  const tabs = [
    { id: 'templates', name: 'Workflow Templates', icon: GitBranch, badge: summary?.total_workflows || 0 },
    { id: 'assignments', name: 'Assignment Matrix', icon: Target, badge: summary?.total_assignments || 0 },
    { id: 'preview', name: 'Employee Preview', icon: Eye, badge: null },
  ];

  return (
    <div className="space-y-6 md:space-y-8">
      {/* Page Header */}
      <div>
        <div className="flex items-center gap-3 mb-1 min-w-0">
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <GitBranch className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
          </div>
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Approval Workflows</h1>
        </div>
        <p className="text-xs sm:text-sm text-gray-500 ml-11 sm:ml-12">
          Configure leave approval chains and assign them to departments, trades, or individual employees.
        </p>
      </div>

      {/* Summary Metrics Skeleton — cards/widgets loading => card skeletons */}
      {summaryLoading && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 animate-pulse">
              <div className="flex items-center">
                <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex-shrink-0"></div>
                <div className="ml-2 sm:ml-4 space-y-2 min-w-0">
                  <div className="h-6 bg-gray-200 rounded w-16"></div>
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary Metrics — Admin Theme (Red) per design-system-reference.md §Metrics Cards */}
      {!summaryLoading && summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
            <div className="flex items-center">
              <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <GitBranch className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
              </div>
              <div className="ml-2 sm:ml-4 min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{summary.total_workflows || 0}</p>
                <p className="text-xs sm:text-sm text-gray-600 truncate">Active Workflows</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
            <div className="flex items-center">
              <div className="w-9 h-9 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Target className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
              </div>
              <div className="ml-2 sm:ml-4 min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{summary.total_assignments || 0}</p>
                <p className="text-xs sm:text-sm text-gray-600 truncate">Active Assignments</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
            <div className="flex items-center">
              <div className="w-9 h-9 sm:w-12 sm:h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
              </div>
              <div className="ml-2 sm:ml-4 min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-gray-900">
                  {(summary.assignment_counts?.department || 0) + (summary.assignment_counts?.trade_category || 0)}
                </p>
                <p className="text-xs sm:text-sm text-gray-600 truncate">Org-Level Rules</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
            <div className="flex items-center">
              <div className="w-9 h-9 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <UserCheck className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
              </div>
              <div className="ml-2 sm:ml-4 min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{summary.assignment_counts?.individual || 0}</p>
                <p className="text-xs sm:text-sm text-gray-600 truncate">Individual Overrides</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation — Containerless Pill Tabs per design-system-reference.md §Tab Navigation */}
      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`tab-btn-admin ${isActive ? 'active' : ''}`}
              title={tab.name}
            >
              <Icon className="w-4 h-4" />
              {tab.name}
              {tab.badge !== null && tab.badge > 0 && (
                <CountBadge count={tab.badge} theme="admin" className="ml-1" />
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'templates' && <WorkflowTemplatesTab />}
      {activeTab === 'assignments' && <AssignmentMatrixTab />}
      {activeTab === 'preview' && <EmployeePreviewTab />}
    </div>
  );
};

// ─── Tab 1: Workflow Templates ──────────────────────────────────────────

const WorkflowTemplatesTab = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [classificationFilter, setClassificationFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [versionHistoryId, setVersionHistoryId] = useState(null);

  const { data: workflows = [], isLoading } = useApprovalWorkflows({
    active_only: 'false',
    ...(classificationFilter ? { classification: classificationFilter } : {}),
  });

  const filteredWorkflows = useMemo(() => {
    if (!searchTerm) return workflows;
    const q = searchTerm.toLowerCase();
    return workflows.filter(
      (w) =>
        w.workflow_name.toLowerCase().includes(q) ||
        w.employee_classification.toLowerCase().includes(q) ||
        (w.description || '').toLowerCase().includes(q)
    );
  }, [workflows, searchTerm]);

  // Group by workflow_group_id — show latest version per group
  const groupedWorkflows = useMemo(() => {
    const groups = {};
    filteredWorkflows.forEach((w) => {
      if (!groups[w.workflow_group_id] || w.version_number > groups[w.workflow_group_id].version_number) {
        groups[w.workflow_group_id] = w;
      }
    });
    return Object.values(groups);
  }, [filteredWorkflows]);

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div>
        <div className="flex items-center justify-between gap-3">
          <DebouncedSearchInput
            value={searchTerm}
            onSearch={setSearchTerm}
            placeholder="Search workflows..."
            className="max-w-xl"
          />
          <div className="flex items-center gap-2">
            <FilterButton
              theme="admin"
              isOpen={showFilters}
              activeCount={classificationFilter ? 1 : 0}
              onClick={() => setShowFilters(v => !v)}
            />
            <button
              onClick={() => { setEditingWorkflow(null); setShowCreateForm(true); }}
              className="btn btn-admin-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Workflow
            </button>
          </div>
        </div>

        {/* Collapsible filter panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="form-label-admin">Classification</label>
                <PillSelect
                  theme="admin"
                  value={classificationFilter}
                  onChange={setClassificationFilter}
                  placeholder="All Classifications"
                  icon={Users}
                  iconColor="text-red-600"
                  className="w-full"
                  options={[
                    { value: '', label: 'All Classifications' },
                    ...Object.entries(CLASSIFICATION_LABELS).map(([k, v]) => ({ value: k, label: v })),
                  ]}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Workflow Cards */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : groupedWorkflows.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border p-12 text-center">
          <GitBranch className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg font-medium">No workflows found</p>
          <p className="text-gray-400 text-sm mt-1">Create your first approval workflow to get started.</p>
          <button
            onClick={() => { setEditingWorkflow(null); setShowCreateForm(true); }}
            className="btn btn-admin-primary mt-4"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Workflow
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {groupedWorkflows.map((workflow) => (
            <WorkflowCard
              key={workflow.id}
              workflow={workflow}
              onEdit={() => { setEditingWorkflow(workflow); setShowCreateForm(true); }}
              onViewVersions={() => setVersionHistoryId(workflow.id)}
            />
          ))}
        </div>
      )}

      {/* Create / Edit Form Modal */}
      {showCreateForm && (
        <WorkflowFormModal
          workflow={editingWorkflow}
          onClose={() => { setShowCreateForm(false); setEditingWorkflow(null); }}
        />
      )}

      {/* Version History Panel */}
      {versionHistoryId && (
        <VersionHistoryModal
          workflowId={versionHistoryId}
          onClose={() => setVersionHistoryId(null)}
        />
      )}
    </div>
  );
};

// ─── Workflow Card ──────────────────────────────────────────────────────

const WorkflowCard = ({ workflow, onEdit, onViewVersions }) => {
  const deleteWorkflow = useDeleteWorkflow();
  const duplicateWorkflow = useDuplicateWorkflow();
  const [showDuplicateInput, setShowDuplicateInput] = useState(false);
  const [newEffectiveFrom, setNewEffectiveFrom] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const hierarchy = workflow.approval_hierarchy || [];
  const isInUse = workflow.is_in_use;
  const isAssigned = workflow.is_assigned;

  const handleDuplicate = () => {
    if (!newEffectiveFrom) {
      toast.error('Please enter an effective-from date');
      return;
    }
    duplicateWorkflow.mutate(
      { id: workflow.id, data: { effective_from: newEffectiveFrom } },
      { onSuccess: () => setShowDuplicateInput(false) }
    );
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-5 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-semibold text-gray-900 truncate">{workflow.workflow_name}</h3>
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
              v{workflow.version_number}
            </span>
            {workflow.is_active ? (
              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 inline-flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> Active
              </span>
            ) : (
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Inactive</span>
            )}
            {isAssigned && !isInUse && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 inline-flex items-center gap-1" title="Mapped to employees via Assignment Matrix">
                <Target className="w-3 h-3" /> Assigned
              </span>
            )}
            {isInUse && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 inline-flex items-center gap-1" title="Referenced by leave applications - cannot be edited">
                <Shield className="w-3 h-3" /> In Use
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
            <span className="inline-flex items-center gap-1">
              <Users className="w-3.5 h-3.5" />
              {CLASSIFICATION_LABELS[workflow.employee_classification] || workflow.employee_classification}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              Effective {formatDate(workflow.effective_from)}
              {workflow.effective_to && ` → ${formatDate(workflow.effective_to)}`}
            </span>
          </div>
          {workflow.description && (
            <p className="mt-1 text-xs text-gray-400 truncate">{workflow.description}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 ml-3 flex-shrink-0">
          <button
            onClick={onViewVersions}
            className="icon-button-admin"
            title="Version History"
            aria-label="Version History"
          >
            <History className="w-4 h-4" />
          </button>
          <button
            onClick={onEdit}
            className="icon-button-admin"
            title={isInUse ? 'View (read-only — in use)' : 'Edit'}
            aria-label={isInUse ? 'View (read-only — in use)' : 'Edit'}
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowDuplicateInput(!showDuplicateInput)}
            className="icon-button-admin"
            title="Create New Version"
            aria-label="Create New Version"
          >
            <Copy className="w-4 h-4" />
          </button>
          {!isInUse && (
            <button
              onClick={() => setConfirmDelete(true)}
              className="icon-button-danger"
              title="Delete"
              aria-label="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Approval Chain Visualizer */}
      <div className="mt-4 flex items-center gap-1 overflow-x-auto pb-1">
        {hierarchy.map((step, idx) => {
          const thresholdLabel = formatThreshold(step.threshold_days);
          return (
            <React.Fragment key={step.level}>
              <div
                className="flex-shrink-0 px-3 py-1.5 rounded-full bg-red-50 border border-red-200 text-xs font-medium text-red-700 inline-flex items-center gap-1.5"
                title={thresholdLabel ? `Applies for leave up to ${step.threshold_days} days` : 'Applies for all leave durations'}
              >
                <span className="w-5 h-5 bg-red-600 text-white rounded-full flex items-center justify-center text-[10px] font-bold">
                  {step.level}
                </span>
                {APPROVER_ROLE_LABELS[step.role] || step.role}
                {thresholdLabel && (
                  <span className="text-red-400 text-[10px]">{thresholdLabel}</span>
                )}
              </div>
              {idx < hierarchy.length - 1 && (
                <ArrowRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
              )}
            </React.Fragment>
          );
        })}
        {hierarchy.length === 0 && (
          <span className="text-xs text-gray-400 italic">No approval levels configured</span>
        )}
      </div>

      {/* Duplicate Input — inline */}
      {showDuplicateInput && (
        <div className="mt-3 flex items-center gap-2 p-3 bg-blue-50 rounded-xl border border-blue-200">
          <label className="text-xs font-medium text-blue-700 whitespace-nowrap">New effective from:</label>
          <div className="flex-1">
            <DatePickerField
              selected={parseLocalDate(newEffectiveFrom)}
              onChange={(date) => setNewEffectiveFrom(date ? toLocalISODate(date) : '')}
              className="input-admin text-sm"
              placeholderText="Select date"
            />
          </div>
          <button
            onClick={handleDuplicate}
            disabled={duplicateWorkflow.isPending}
            className="btn btn-admin-primary btn-sm text-xs"
          >
            {duplicateWorkflow.isPending ? 'Creating...' : 'Create Version'}
          </button>
          <button onClick={() => setShowDuplicateInput(false)} className="p-1 text-gray-400 hover:text-gray-600 rounded-full">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmDelete}
        title="Delete Workflow"
        message={`Are you sure you want to delete "${workflow.workflow_name}" v${workflow.version_number}?`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => {
          deleteWorkflow.mutate(workflow.id);
          setConfirmDelete(false);
        }}
        onCancel={() => setConfirmDelete(false)}
        theme="admin"
      />
    </div>
  );
};

// ─── Version History Modal ──────────────────────────────────────────────

const VersionHistoryModal = ({ workflowId, onClose }) => {
  const { data: versions = [], isLoading } = useWorkflowVersionHistory(workflowId);

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      theme="admin"
      className="max-w-2xl"
      customHeader={(
        <div className="modal-header">
          <h2 className="modal-title flex items-center gap-2 min-w-0 truncate">
            <History className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span className="truncate">Version History</span>
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    >
            {isLoading ? (
              <div className="flex justify-center py-8"><LoadingSpinner /></div>
            ) : versions.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No version history found.</p>
            ) : (
              <div className="space-y-3">
                {versions.map((v, idx) => (
                  <div
                    key={v.id}
                    className={`p-4 rounded-xl border ${idx === 0 ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">v{v.version_number}</span>
                        {idx === 0 && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-600 text-white">Current</span>
                        )}
                        {v.is_in_use && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">In Use</span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {formatDate(v.effective_from)}{v.effective_to ? ` → ${formatDate(v.effective_to)}` : ' → Present'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{v.description || 'No description'}</p>
                    <div className="flex items-center gap-1 mt-2">
                      {(v.approval_hierarchy || []).map((step, i) => (
                        <React.Fragment key={step.level}>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                            {APPROVER_ROLE_LABELS[step.role] || step.role}
                          </span>
                          {i < (v.approval_hierarchy || []).length - 1 && (
                            <ArrowRight className="w-3 h-3 text-gray-300" />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
    </Modal>
  );
};

// ─── Workflow Form Modal (Create / Edit) ────────────────────────────────

const EMPTY_FORM = {
  workflow_name: '',
  description: '',
  employee_classification: 'staff',
  effective_from: getTodayInSingaporeTimezone(),
  effective_to: '',
  first_approver_role: 'reporting_officer',
  second_approver_role: '',
  third_approver_role: '',
  fourth_approver_role: '',
  first_approval_threshold_days: 7,
  second_approval_threshold_days: 30,
  third_approval_threshold_days: 60,
  fourth_approval_threshold_days: 90,
  auto_escalate_after_hours: 48,
  escalation_approver_role: '',
  is_active: true,
};

const WorkflowFormModal = ({ workflow, onClose }) => {
  const isEditing = !!workflow;
  const isReadOnly = isEditing && workflow.is_in_use;

  const [form, setForm] = useState(() => {
    if (workflow) {
      return {
        workflow_name: workflow.workflow_name || '',
        description: workflow.description || '',
        employee_classification: workflow.employee_classification || 'staff',
        effective_from: workflow.effective_from || '',
        effective_to: workflow.effective_to || '',
        first_approver_role: workflow.first_approver_role || '',
        second_approver_role: workflow.second_approver_role || '',
        third_approver_role: workflow.third_approver_role || '',
        fourth_approver_role: workflow.fourth_approver_role || '',
        first_approval_threshold_days: workflow.first_approval_threshold_days ?? 7,
        second_approval_threshold_days: workflow.second_approval_threshold_days ?? 30,
        third_approval_threshold_days: workflow.third_approval_threshold_days ?? 60,
        fourth_approval_threshold_days: workflow.fourth_approval_threshold_days ?? 90,
        auto_escalate_after_hours: workflow.auto_escalate_after_hours ?? 48,
        escalation_approver_role: workflow.escalation_approver_role || '',
        is_active: workflow.is_active ?? true,
      };
    }
    return { ...EMPTY_FORM };
  });

  const createWorkflow = useCreateWorkflow();
  const updateWorkflow = useUpdateWorkflow();

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (!payload.effective_to) delete payload.effective_to;

    if (isEditing) {
      updateWorkflow.mutate({ id: workflow.id, data: payload }, { onSuccess: onClose });
    } else {
      createWorkflow.mutate(payload, { onSuccess: onClose });
    }
  };

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const isSaving = createWorkflow.isPending || updateWorkflow.isPending;

  // Count active approval levels
  const levelCount = [form.first_approver_role, form.second_approver_role, form.third_approver_role, form.fourth_approver_role].filter(Boolean).length;

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      theme="admin"
      className="max-w-3xl"
      contentClassName="!p-0"
      customHeader={(
        <div className="modal-header">
          <h2 className="modal-title flex items-center gap-2 min-w-0 truncate">
            <GitBranch className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span className="truncate">{isReadOnly ? 'View Workflow' : isEditing ? 'Edit Workflow' : 'New Workflow'}</span>
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    >
          {isReadOnly && (
            <div className="mx-5 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2 text-sm text-amber-700">
              <AlertTriangle className="w-4 h-4" />
              This workflow is referenced by leave applications and cannot be edited. Create a new version instead.
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-5 overflow-y-auto overscroll-contain max-h-[70vh] space-y-5">
            {/* Section 1: Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">Workflow Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.workflow_name}
                  onChange={(e) => set('workflow_name', e.target.value)}
                  className="input-admin w-full"
                  placeholder="e.g. Staff Leave Approval"
                  required
                  disabled={isReadOnly}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Classification <span className="text-red-500">*</span></label>
                <FormSelect
                  theme="admin"
                  value={form.employee_classification}
                  onChange={(val) => set('employee_classification', val)}
                  options={Object.entries(CLASSIFICATION_LABELS).map(([k, v]) => ({ value: k, label: v }))}
                  disabled={isReadOnly}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                className="input-admin w-full"
                rows={2}
                placeholder="Optional — describe this version or what changed"
                disabled={isReadOnly}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">Effective From <span className="text-red-500">*</span></label>
                <DatePickerField
                  selected={parseLocalDate(form.effective_from)}
                  onChange={(date) => set('effective_from', date ? toLocalISODate(date) : '')}
                  placeholderText="Select date"
                  required
                  disabled={isReadOnly}
                  maxDate={parseLocalDate(form.effective_to)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Effective To</label>
                <DatePickerField
                  selected={parseLocalDate(form.effective_to)}
                  onChange={(date) => set('effective_to', date ? toLocalISODate(date) : '')}
                  placeholderText="Select date"
                  isClearable
                  disabled={isReadOnly}
                  minDate={parseLocalDate(form.effective_from)}
                />
                <p className="text-xs text-gray-400 mt-1">Leave empty for no end date (current)</p>
              </div>
            </div>

            {/* Section 2: Approval Hierarchy */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Layers className="w-4 h-4 text-red-500" />
                Approval Hierarchy ({levelCount} level{levelCount !== 1 ? 's' : ''})
              </h3>
              <div className="space-y-3">
                {[
                  { level: 1, roleField: 'first_approver_role', thresholdField: 'first_approval_threshold_days' },
                  { level: 2, roleField: 'second_approver_role', thresholdField: 'second_approval_threshold_days' },
                  { level: 3, roleField: 'third_approver_role', thresholdField: 'third_approval_threshold_days' },
                  { level: 4, roleField: 'fourth_approver_role', thresholdField: 'fourth_approval_threshold_days' },
                ].map(({ level, roleField, thresholdField }) => (
                  <div key={level} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border">
                    <span className="w-7 h-7 bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {level}
                    </span>
                    <div className="flex-1">
                      <FormSelect
                        theme="admin"
                        value={form[roleField]}
                        onChange={(val) => set(roleField, val)}
                        placeholder="— Not Set —"
                        options={[
                          { value: '', label: '— Not Set —' },
                          ...Object.entries(APPROVER_ROLE_LABELS).map(([k, v]) => ({ value: k, label: v })),
                        ]}
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className="flex items-center gap-1.5" title="Max leave days this level handles (999 = all)">
                      <span className="text-xs text-gray-500 whitespace-nowrap">up to</span>
                      <input
                        type="number"
                        value={form[thresholdField]}
                        onChange={(e) => set(thresholdField, parseInt(e.target.value) || 0)}
                        className="input-admin w-16 text-center text-sm"
                        min={0}
                        disabled={isReadOnly || !form[roleField]}
                      />
                      <span className="text-xs text-gray-500">days</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 3: Escalation */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">Auto-Escalate After (hours)</label>
                <input
                  type="number"
                  value={form.auto_escalate_after_hours}
                  onChange={(e) => set('auto_escalate_after_hours', parseInt(e.target.value) || 0)}
                  className="input-admin w-full"
                  min={0}
                  disabled={isReadOnly}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Escalation Approver</label>
                <FormSelect
                  theme="admin"
                  value={form.escalation_approver_role}
                  onChange={(val) => set('escalation_approver_role', val)}
                  placeholder="— None —"
                  options={[
                    { value: '', label: '— None —' },
                    { value: 'department_head', label: 'Department Head' },
                    { value: 'hr_manager', label: 'HR Manager' },
                    { value: 'finance_manager', label: 'Finance Manager' },
                  ]}
                  disabled={isReadOnly}
                />
              </div>
            </div>

            {/* Active toggle */}
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => set('is_active', e.target.checked)}
                  className="sr-only peer"
                  disabled={isReadOnly}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-red-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600" />
              </label>
              <span className="text-sm text-gray-700">Active</span>
            </div>

            {/* Submit */}
            {!isReadOnly && (
              <div className="flex items-center justify-end gap-3 pt-2 border-t">
                <button type="button" onClick={onClose} className="btn btn-admin-outline">
                  Cancel
                </button>
                <button type="submit" disabled={isSaving} className="btn btn-admin-primary">
                  {isSaving ? 'Saving...' : isEditing ? 'Update Workflow' : 'Create Workflow'}
                </button>
              </div>
            )}
          </form>
    </Modal>
  );
};

// ─── Tab 2: Assignment Matrix ───────────────────────────────────────────

const AssignmentMatrixTab = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [confirmRevokeStaffId, setConfirmRevokeStaffId] = useState(null);

  const { data: assignments = [], isLoading } = useWorkflowAssignments();
  const { data: selfApproveData, refetch: refetchSelfApprove } = useStaffList(
    { self_approve_leave: true, page_size: 500 },
    { staleTime: 0 }
  );
  const selfApproveStaff = selfApproveData?.results || [];
  const deleteAssignment = useDeleteAssignment();
  const updateStaff = useUpdateStaff();
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const handleDeleteAssignment = () => {
    if (confirmDeleteId) {
      deleteAssignment.mutate(confirmDeleteId);
      setConfirmDeleteId(null);
    }
  };

  const handleRevokeConfirm = () => {
    if (confirmRevokeStaffId) {
      updateStaff.mutate(
        { staffId: confirmRevokeStaffId, data: { self_approve_leave: false } },
        {
          onSuccess: () => {
            toast.success(`Self-approve override revoked for ${confirmRevokeStaffId}`);
            refetchSelfApprove();
          },
          onError: () => toast.error('Failed to revoke self-approve override'),
        }
      );
      setConfirmRevokeStaffId(null);
    }
  };

  const filteredAssignments = useMemo(() => {
    if (!typeFilter) return assignments;
    return assignments.filter((a) => a.assignment_type === typeFilter);
  }, [assignments, typeFilter]);

  // Group by assignment_type for visual clarity
  const grouped = useMemo(() => {
    const order = ['individual', 'department', 'trade_category', 'classification_default'];
    const map = {};
    order.forEach((t) => { map[t] = []; });
    filteredAssignments.forEach((a) => {
      if (map[a.assignment_type]) map[a.assignment_type].push(a);
      else map[a.assignment_type] = [a];
    });
    return order.map((t) => ({ type: t, items: map[t] })).filter((g) => g.items.length > 0);
  }, [filteredAssignments]);

  return (
    <div className="space-y-4">
      {/* Resolution Priority Legend */}
      <div className="bg-white rounded-2xl shadow-sm border p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Info className="w-4 h-4 text-red-500" />
          Resolution Priority Chain
        </h3>
        <div className="flex items-center gap-2 flex-wrap text-xs">
          {[
            { label: '1. Individual Override', color: 'bg-purple-100 text-purple-700 border-purple-200', icon: UserCheck },
            { label: '2. Department', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Building2 },
            { label: '3. Trade Category', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Wrench },
            { label: '4. Classification Default', color: 'bg-gray-100 text-gray-600 border-gray-200', icon: Users },
          ].map((item, idx) => (
            <React.Fragment key={item.label}>
              <span className={`px-3 py-1.5 rounded-full border font-medium inline-flex items-center gap-1.5 ${item.color}`}>
                <item.icon className="w-3.5 h-3.5" />
                {item.label}
              </span>
              {idx < 3 && <ArrowRight className="w-4 h-4 text-gray-300" />}
            </React.Fragment>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-2">
          When an employee submits leave, the system checks assignments in this order. The first match wins.
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3">
        <PillSelect
          theme="admin"
          value={typeFilter}
          onChange={setTypeFilter}
          placeholder="All Assignment Types"
          icon={Target}
          iconColor="text-red-600"
          options={[
            { value: '', label: 'All Assignment Types' },
            ...Object.entries(ASSIGNMENT_TYPE_LABELS).map(([k, v]) => ({ value: k, label: v })),
          ]}
        />
        <button
          onClick={() => setShowCreateForm(true)}
          className="btn btn-admin-primary flex-shrink-0"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Assignment
        </button>
      </div>

      {/* Assignment Groups */}
      {isLoading ? (
        <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>
      ) : grouped.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border p-12 text-center">
          <Target className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg font-medium">No assignments configured</p>
          <p className="text-gray-400 text-sm mt-1">Assign workflows to departments, trades, or employees.</p>
          <button onClick={() => setShowCreateForm(true)} className="btn btn-admin-primary mt-4">
            <Plus className="w-4 h-4 mr-2" />
            Create Assignment
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map((group) => (
            <div key={group.type}>
              <h3 className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-full text-xs ${PRIORITY_COLORS[group.type]}`}>
                  {ASSIGNMENT_TYPE_LABELS[group.type]}
                </span>
                <span className="text-gray-400">({group.items.length})</span>
              </h3>
              <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
                {/* Mobile card view */}
                <div className="md:hidden p-4 space-y-3">
                  {group.items.map((a) => (
                    <div key={a.id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col h-full">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{a.target_display}</p>
                          <p className="text-xs text-gray-500 truncate">{a.workflow_name}</p>
                        </div>
                        <div className="flex-shrink-0">{renderAssignmentStatusBadge(a)}</div>
                      </div>
                      <div className="mt-3 space-y-1.5 flex-1">
                        <div className="flex items-start justify-between gap-3 text-sm">
                          <span className="text-gray-500 flex-shrink-0">Reason</span>
                          <span className="text-gray-900 text-right truncate min-w-0">{a.override_reason || '—'}</span>
                        </div>
                        <div className="flex items-start justify-between gap-3 text-sm">
                          <span className="text-gray-500 flex-shrink-0">Expires</span>
                          <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">{a.expires_at ? formatDate(a.expires_at) : '—'}</span>
                        </div>
                      </div>
                      <div className="card-action-footer">
                        <button
                          onClick={() => setConfirmDeleteId(a.id)}
                          className="icon-button-admin"
                          title="Remove assignment"
                          aria-label="Remove assignment"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop table view */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm table-fixed">
                    <thead className="table-header">
                      <tr>
                        <th className="table-header-cell w-[22%]">Target</th>
                        <th className="table-header-cell w-[35%]">Workflow</th>
                        <th className="table-header-cell w-[13%]">Reason</th>
                        <th className="table-header-cell text-right w-[12%]">Expires</th>
                        <th className="table-header-cell text-center w-[10%]">Status</th>
                        <th className="table-header-cell text-right w-[8%]"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {group.items.map((a) => (
                        <tr key={a.id} className="table-row">
                          <td className="px-4 py-3 font-medium text-gray-900 w-[22%]">{a.target_display}</td>
                          <td className="px-4 py-3 text-gray-600 w-[35%]">{a.workflow_name}</td>
                          <td className="px-4 py-3 text-gray-500 text-xs w-[13%]">{a.override_reason || '—'}</td>
                          <td className="px-4 py-3 text-gray-500 text-xs text-right w-[12%]">{a.expires_at ? formatDate(a.expires_at) : '—'}</td>
                          <td className="px-4 py-3 text-center">
                            {renderAssignmentStatusBadge(a)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => setConfirmDeleteId(a.id)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                              title="Remove"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Self-Approve Overrides Section */}
      {selfApproveStaff.length > 0 && (
        <div className="mt-2">
          <h3 className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
            <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700 inline-flex items-center gap-1">
              <Shield className="w-3 h-3" />
              Self-Approve Overrides
            </span>
            <span className="text-gray-400">({selfApproveStaff.length})</span>
          </h3>
          <div className="bg-amber-50 rounded-2xl shadow-sm border border-amber-200 overflow-hidden">
            <table className="w-full text-sm table-fixed">
              <thead>
                <tr className="bg-amber-100 text-left">
                  <th className="px-4 py-3 font-medium text-amber-800 w-[40%]">Employee</th>
                  <th className="px-4 py-3 font-medium text-amber-800 w-[30%]">Classification</th>
                  <th className="px-4 py-3 font-medium text-amber-800 w-[20%]">Note</th>
                  <th className="px-4 py-3 font-medium text-amber-800 text-right w-[10%]"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-amber-100">
                {selfApproveStaff.map((emp) => (
                  <tr key={emp.staff_id} className="hover:bg-amber-100/50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {emp.alias?.trim()
                        ? `${emp.alias.trim()} (${emp.first_name} ${emp.last_name})`
                        : `${emp.first_name} ${emp.last_name}`}
                      <span className="ml-2 text-xs text-gray-400">{emp.staff_id}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs capitalize">
                      {emp.employment_classification || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-200 text-amber-800">
                        Auto-approved
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setConfirmRevokeStaffId(emp.staff_id)}
                        className="p-1.5 text-amber-600 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                        title="Revoke self-approve override"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Assignment Modal */}
      {showCreateForm && (
        <AssignmentFormModal onClose={() => { setShowCreateForm(false); refetchSelfApprove(); }} />
      )}

      <ConfirmModal
        isOpen={!!confirmDeleteId}
        title="Remove Assignment"
        message="Are you sure you want to remove this assignment?"
        confirmLabel="Remove"
        variant="danger"
        onConfirm={handleDeleteAssignment}
        onCancel={() => setConfirmDeleteId(null)}
        theme="admin"
      />

      <ConfirmModal
        isOpen={!!confirmRevokeStaffId}
        title="Revoke Self-Approve Override"
        message={`Are you sure you want to revoke the self-approve override for ${confirmRevokeStaffId}? Their leave will go through the normal workflow again.`}
        confirmLabel="Revoke"
        variant="danger"
        onConfirm={handleRevokeConfirm}
        onCancel={() => setConfirmRevokeStaffId(null)}
        theme="admin"
      />
    </div>
  );
};

// ─── Assignment Form Modal ──────────────────────────────────────────────

const AssignmentFormModal = ({ onClose }) => {
  const [form, setForm] = useState({
    assignment_type: 'classification_default',
    workflow: '',
    employee_classification: 'staff',
    department: '',
    trade_category: '',
    employee: '',
    override_reason: 'standard',
    override_notes: '',
    expires_at: '',
    self_approve: false,
  });

  const { data: workflows = [] } = useActiveWorkflows();
  const { data: departments = [] } = useDepartments();
  const { data: trades = [] } = useTradeCategories();
  const { data: staffData } = useStaffList({ page_size: 500 });
  const staffOptions = staffData?.results || [];
  const createAssignment = useCreateAssignment();
  const updateStaff = useUpdateStaff();

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const isSelfApprove = form.assignment_type === 'individual' && form.self_approve;

  const handleSubmit = (e) => {
    e.preventDefault();

    if (isSelfApprove) {
      // Patch the employee flag directly — no WorkflowAssignment record needed
      updateStaff.mutate(
        { staffId: form.employee, data: { self_approve_leave: true } },
        {
          onSuccess: () => {
            toast.success(`Self-approve override enabled for ${form.employee}`);
            onClose();
          },
          onError: () => {
            toast.error('Failed to enable self-approve override');
          },
        }
      );
      return;
    }

    const payload = { ...form };
    delete payload.self_approve;
    // Clean up fields not relevant to this assignment type
    if (payload.assignment_type !== 'classification_default') delete payload.employee_classification;
    if (payload.assignment_type !== 'department') payload.department = null;
    if (payload.assignment_type !== 'trade_category') payload.trade_category = null;
    if (payload.assignment_type !== 'individual') payload.employee = null;
    if (!payload.expires_at) delete payload.expires_at;

    createAssignment.mutate(payload, { onSuccess: onClose });
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="md"
      theme="admin"
      className="max-w-lg"
      contentClassName="!p-0"
      customHeader={(
        <div className="modal-header">
          <h2 className="modal-title flex items-center gap-2 min-w-0 truncate">
            <Target className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span className="truncate">New Assignment</span>
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    >
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            {/* Assignment Type */}
            <div className="form-group">
              <label className="form-label">Assignment Type <span className="text-red-500">*</span></label>
              <FormSelect
                theme="admin"
                value={form.assignment_type}
                onChange={(val) => set('assignment_type', val)}
                options={Object.entries(ASSIGNMENT_TYPE_LABELS).map(([k, v]) => ({ value: k, label: v }))}
                required
              />
            </div>

            {/* Target field — dynamic based on type */}
            {form.assignment_type === 'classification_default' && (
              <div className="form-group">
                <label className="form-label">Classification <span className="text-red-500">*</span></label>
                <FormSelect
                  theme="admin"
                  value={form.employee_classification}
                  onChange={(val) => set('employee_classification', val)}
                  options={Object.entries(CLASSIFICATION_LABELS).map(([k, v]) => ({ value: k, label: v }))}
                  required
                />
              </div>
            )}
            {form.assignment_type === 'department' && (
              <div className="form-group">
                <label className="form-label">Department <span className="text-red-500">*</span></label>
                <FormSelect
                  theme="admin"
                  value={form.department}
                  onChange={(val) => set('department', val)}
                  placeholder="Select department..."
                  options={departments.map((d) => ({ value: d.id, label: d.department_name }))}
                  required
                />
              </div>
            )}
            {form.assignment_type === 'trade_category' && (
              <div className="form-group">
                <label className="form-label">Trade Category <span className="text-red-500">*</span></label>
                <FormSelect
                  theme="admin"
                  value={form.trade_category}
                  onChange={(val) => set('trade_category', val)}
                  placeholder="Select trade..."
                  options={trades.map((t) => ({ value: t.id, label: t.trade_code || t.name }))}
                  required
                />
              </div>
            )}
            {form.assignment_type === 'individual' && (
              <>
                <div className="form-group">
                  <label className="form-label">Employee <span className="text-red-500">*</span></label>
                  <SearchableCombobox
                    theme="admin"
                    variant="form"
                    items={staffOptions}
                    value={form.employee}
                    onChange={(val) => set('employee', val)}
                    placeholder="Select employee..."
                    searchPlaceholder="Search name or Staff ID..."
                    valueFn={(emp) => emp.staff_id}
                    displayFn={(emp) =>
                      `${emp.alias?.trim()
                        ? `${emp.alias.trim()} (${emp.first_name} ${emp.last_name})`
                        : `${emp.first_name} ${emp.last_name}`} — ${emp.staff_id}`
                    }
                    searchFn={(emp, term) =>
                      `${emp.alias || ''} ${emp.first_name} ${emp.last_name} ${emp.staff_id}`
                        .toLowerCase()
                        .includes(term)
                    }
                  />
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50">
                  <input
                    id="self_approve_checkbox"
                    type="checkbox"
                    checked={form.self_approve}
                    onChange={(e) => set('self_approve', e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-red-600 focus-visible:ring-red-500"
                  />
                  <div>
                    <label htmlFor="self_approve_checkbox" className="text-sm font-medium text-amber-900 cursor-pointer select-none">
                      Self-Approve Override
                    </label>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Leave applications submitted by this employee will be auto-approved immediately, bypassing all workflow steps.
                    </p>
                  </div>
                </div>
              </>
            )}

            {/* Workflow Selection — hidden when self-approve override is active */}
            {!isSelfApprove && (
            <div className="form-group">
              <label className="form-label">Workflow <span className="text-red-500">*</span></label>
              <FormSelect
                theme="admin"
                value={form.workflow}
                onChange={(val) => set('workflow', val)}
                placeholder="Select workflow..."
                options={workflows.map((w) => ({
                  value: w.id,
                  label: `${w.workflow_name} v${w.version_number} (${CLASSIFICATION_LABELS[w.employee_classification] || w.employee_classification})`,
                }))}
                required
              />
            </div>
            )}

            {/* Override reason (for individual/department) — hidden when self-approve is active */}
            {!isSelfApprove && (form.assignment_type === 'individual' || form.assignment_type === 'department') && (
              <>
                <div className="form-group">
                  <label className="form-label">Reason</label>
                  <FormSelect
                    theme="admin"
                    value={form.override_reason}
                    onChange={(val) => set('override_reason', val)}
                    options={[
                      { value: 'standard', label: 'Standard Assignment' },
                      { value: 'secondment', label: 'Secondment' },
                      { value: 'disciplinary', label: 'Disciplinary' },
                      { value: 'interim', label: 'Interim Arrangement' },
                      { value: 'other', label: 'Other' },
                    ]}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea
                    value={form.override_notes}
                    onChange={(e) => set('override_notes', e.target.value)}
                    className="input-admin w-full"
                    rows={2}
                    placeholder="Optional notes for audit trail"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Expires At</label>
                  <DatePickerField
                    selected={parseLocalDate(form.expires_at)}
                    onChange={(date) => set('expires_at', date ? toLocalISODate(date) : '')}
                    placeholderText="Select date"
                    isClearable
                  />
                  <p className="text-xs text-gray-400 mt-1">Leave empty for no expiry</p>
                </div>
              </>
            )}

            <div className="flex items-center justify-end gap-3 pt-2 border-t">
              <button type="button" onClick={onClose} className="btn btn-admin-outline">Cancel</button>
              <button
                type="submit"
                disabled={createAssignment.isPending || updateStaff.isPending}
                className="btn btn-admin-primary"
              >
                {isSelfApprove
                  ? (updateStaff.isPending ? 'Saving...' : 'Enable Self-Approve')
                  : (createAssignment.isPending ? 'Creating...' : 'Create Assignment')}
              </button>
            </div>
          </form>
    </Modal>
  );
};

// ─── Tab 3: Employee Preview ────────────────────────────────────────────

const EmployeePreviewTab = () => {
  const [staffId, setStaffId] = useState('');
  const [previewResult, setPreviewResult] = useState(null);
  const previewMutation = usePreviewWorkflowForEmployee();

  const handlePreview = (e) => {
    e.preventDefault();
    if (!staffId.trim()) return;
    previewMutation.mutate(staffId.trim(), {
      onSuccess: (response) => setPreviewResult(response.data),
    });
  };

  const sourceIcons = {
    individual: UserCheck,
    department: Building2,
    trade_category: Wrench,
    classification_default: Users,
    legacy_trade_fk: Wrench,
    auto_approve: Shield,
    none: AlertTriangle,
  };

  const sourceColors = {
    individual: 'bg-purple-100 text-purple-700 border-purple-200',
    department: 'bg-blue-100 text-blue-700 border-blue-200',
    trade_category: 'bg-amber-100 text-amber-700 border-amber-200',
    classification_default: 'bg-gray-100 text-gray-600 border-gray-200',
    legacy_trade_fk: 'bg-amber-100 text-amber-700 border-amber-200',
    auto_approve: 'bg-green-100 text-green-700 border-green-200',
    none: 'bg-red-50 text-red-600 border-red-200',
  };

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="bg-white rounded-2xl shadow-sm border p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Eye className="w-4 h-4 text-red-500" />
          Test Workflow Resolution
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Enter an employee's Staff ID to see which workflow would be assigned when they submit a leave application.
        </p>
        <form onSubmit={handlePreview} className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={staffId}
              onChange={(e) => setStaffId(e.target.value)}
              placeholder="Enter Staff ID (e.g. S0001)"
              className="search-box-admin w-full"
            />
          </div>
          <button type="submit" disabled={previewMutation.isPending} className="btn btn-admin-primary">
            {previewMutation.isPending ? 'Checking...' : 'Preview'}
          </button>
        </form>
      </div>

      {/* Result */}
      {previewResult && (
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          {/* Employee Info Header */}
          <div className="p-5 border-b bg-gray-50">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center text-red-600 font-bold text-lg">
                {(previewResult.employee_name || '?')[0]}
              </div>
              <div>
                <p className="font-semibold text-gray-900">{previewResult.employee_name}</p>
                <div className="flex items-center gap-3 text-sm text-gray-500 mt-0.5">
                  <span>{previewResult.staff_id}</span>
                  {previewResult.classification && (
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-xs">
                      {CLASSIFICATION_LABELS[previewResult.classification] || previewResult.classification}
                    </span>
                  )}
                  {previewResult.department && (
                    <span className="inline-flex items-center gap-1">
                      <Building2 className="w-3.5 h-3.5" /> {previewResult.department}
                    </span>
                  )}
                  {previewResult.trade_category && (
                    <span className="inline-flex items-center gap-1">
                      <Wrench className="w-3.5 h-3.5" /> {previewResult.trade_category}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Resolution Result */}
          <div className="p-5">
            <div className="flex items-start gap-4">
              {/* Source Badge */}
              {(() => {
                const SourceIcon = sourceIcons[previewResult.source] || AlertTriangle;
                const colorClass = sourceColors[previewResult.source] || sourceColors.none;
                return (
                  <div className={`px-4 py-2 rounded-xl border font-medium text-sm inline-flex items-center gap-2 ${colorClass}`}>
                    <SourceIcon className="w-4 h-4" />
                    {previewResult.reason}
                  </div>
                );
              })()}
            </div>

            {/* Workflow Detail */}
            {previewResult.workflow ? (
              <div className="mt-4 p-4 rounded-xl bg-gray-50 border">
                <div className="flex items-center gap-2 mb-3">
                  <GitBranch className="w-4 h-4 text-red-600" />
                  <span className="font-semibold text-gray-900">{previewResult.workflow.workflow_name}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                    v{previewResult.workflow.version_number}
                  </span>
                </div>
                {/* Approval chain */}
                <div className="flex items-center gap-1 flex-wrap">
                  {(previewResult.workflow.approval_hierarchy || []).map((step, idx) => (
                    <React.Fragment key={step.level}>
                      <span className="px-3 py-1 rounded-full bg-red-50 border border-red-200 text-xs font-medium text-red-700 inline-flex items-center gap-1">
                        <span className="w-4 h-4 bg-red-600 text-white rounded-full flex items-center justify-center text-[10px] font-bold">
                          {step.level}
                        </span>
                        {APPROVER_ROLE_LABELS[step.role] || step.role}
                      </span>
                      {idx < (previewResult.workflow.approval_hierarchy || []).length - 1 && (
                        <ArrowRight className="w-3 h-3 text-gray-300" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mt-4 p-4 rounded-xl bg-gray-50 border text-sm text-gray-500">
                {previewResult.source === 'auto_approve'
                  ? 'Leave applications are auto-approved for this employee classification.'
                  : 'No workflow assigned — will use single-level Reporting Officer approval.'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalWorkflows;
